import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  BookOpen,
  Download,
  Megaphone,
  Newspaper,
  Pencil,
  Pin,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react'
import { apiJson, type Page } from '../api'
import { hasAnyRole } from '../auth'
import Pager from '../components/Pager'

interface Announcement {
  id: string
  title: string
  body: string
  category: 'news' | 'announcement'
  pinned: boolean
  authorUsername?: string | null
  authorName?: string | null
  publishedAt: string
  expiresAt?: string | null
}

interface Doc {
  id: string
  title: string
  category: string
  fileName: string
  fileSize: number | string
  createdAt: string
}

const categoryMeta: Record<string, { label: string; cls: string }> = {
  news: { label: 'News', cls: 'bg-sky-50 text-sky-700 ring-sky-100' },
  announcement: { label: 'Announcement', cls: 'bg-brand-50 text-brand-700 ring-brand-100' },
}

function fmtDate(iso?: string | null) {
  return iso ? iso.slice(0, 10) : '—'
}

function formatBytes(size: number | string) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function extOf(fileName: string) {
  return (fileName.split('.').pop() ?? '').toUpperCase().slice(0, 4) || 'FILE'
}

export default function Intranet() {
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')
  const [tab, setTab] = useState<'feed' | 'sop'>('feed')

  // ---- news feed ----
  const [feed, setFeed] = useState<Page<Announcement> | null>(null)
  const [feedLoading, setFeedLoading] = useState(true)
  const [feedError, setFeedError] = useState('')
  const [feedQuery, setFeedQuery] = useState('')
  const [feedQ, setFeedQ] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [feedPage, setFeedPage] = useState(1)
  const [feedSize, setFeedSize] = useState(10)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [deletingId, setDeletingId] = useState<string | null>(null)

  // ---- SOP library ----
  const [sopCategory, setSopCategory] = useState<'sop' | 'policy'>('sop')
  const [docs, setDocs] = useState<Page<Doc> | null>(null)
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsError, setDocsError] = useState('')
  const [docQuery, setDocQuery] = useState('')
  const [docQ, setDocQ] = useState('')
  const [docPage, setDocPage] = useState(1)
  const [docSize, setDocSize] = useState(10)

  // debounce search boxes
  useEffect(() => {
    const t = setTimeout(() => { setFeedQ(feedQuery.trim()); setFeedPage(1) }, 300)
    return () => clearTimeout(t)
  }, [feedQuery])
  useEffect(() => {
    const t = setTimeout(() => { setDocQ(docQuery.trim()); setDocPage(1) }, 300)
    return () => clearTimeout(t)
  }, [docQuery])

  const fetchFeed = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(feedPage), size: String(feedSize), sort: 'publishedAt', order: 'DESC' })
      if (feedQ) params.set('q', feedQ)
      if (catFilter) params.set('category', catFilter)
      setFeed(await apiJson<Page<Announcement>>(`/announcements?${params}`))
      setFeedError('')
    } catch (err) {
      setFeedError(err instanceof Error ? err.message : 'Failed to load announcements')
    } finally {
      setFeedLoading(false)
    }
  }, [feedPage, feedSize, feedQ, catFilter])

  useEffect(() => {
    if (tab === 'feed') fetchFeed()
  }, [tab, fetchFeed])

  const fetchDocs = useCallback(async () => {
    setDocsLoading(true)
    try {
      const params = new URLSearchParams({ page: String(docPage), size: String(docSize), sort: 'createdAt', order: 'DESC', category: sopCategory })
      if (docQ) params.set('q', docQ)
      setDocs(await apiJson<Page<Doc>>(`/documents?${params}`))
      setDocsError('')
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setDocsLoading(false)
    }
  }, [docPage, docSize, docQ, sopCategory])

  useEffect(() => {
    if (tab === 'sop') fetchDocs()
  }, [tab, fetchDocs])

  const posts = useMemo(() => feed?.data ?? [], [feed])
  const docRows = useMemo(() => docs?.data ?? [], [docs])

  const remove = async (a: Announcement) => {
    if (!window.confirm(`Delete “${a.title}”?`)) return
    setDeletingId(a.id)
    setFeedError('')
    try {
      await apiJson(`/announcements/${a.id}`, { method: 'DELETE' })
      await fetchFeed()
    } catch (err) {
      setFeedError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const download = async (doc: Doc) => {
    setDocsError('')
    try {
      const data = await apiJson<{ url: string }>(`/documents/${doc.id}/download`)
      window.open(data.url, '_blank', 'noopener')
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  return (
    <div className="space-y-5">
      {/* Tabs + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 sm:w-fit">
          <button
            onClick={() => setTab('feed')}
            className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'feed' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Megaphone size={13} /> News &amp; Announcements
          </button>
          <button
            onClick={() => setTab('sop')}
            className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === 'sop' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <BookOpen size={13} /> SOP Library
          </button>
        </div>
        {tab === 'feed' && allowManage && (
          <button
            onClick={() => navigate('/intranet/new')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Plus size={16} /> New post
          </button>
        )}
        {tab === 'sop' && (
          <button
            onClick={() => navigate('/documents/upload')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Upload size={16} /> Upload document
          </button>
        )}
      </div>

      {/* ---- News & announcements feed ---- */}
      {tab === 'feed' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={feedQuery}
                onChange={(e) => setFeedQuery(e.target.value)}
                placeholder="Search posts…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
              />
            </div>
            <select
              value={catFilter}
              onChange={(e) => { setCatFilter(e.target.value); setFeedPage(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              <option value="">All types</option>
              <option value="news">News</option>
              <option value="announcement">Announcements</option>
            </select>
          </div>

          {feedError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {feedError}
            </div>
          )}

          {feedLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Newspaper size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No posts yet</p>
              <p className="mt-1 text-xs text-slate-400">
                {allowManage ? 'Publish the first announcement to get started.' : 'Company news will appear here.'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {posts.map((a) => {
                  const meta = categoryMeta[a.category] ?? categoryMeta.announcement
                  const isOpen = expanded[a.id]
                  return (
                    <article key={a.id} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            {a.pinned && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-100">
                                <Pin size={10} /> Pinned
                              </span>
                            )}
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${meta.cls}`}>
                              {meta.label}
                            </span>
                            {a.expiresAt && (
                              <span className="text-[11px] text-slate-400">until {fmtDate(a.expiresAt)}</span>
                            )}
                          </div>
                          <h3 className="mt-1.5 text-base font-bold tracking-tight">{a.title}</h3>
                        </div>
                        {allowManage && (
                          <div className="inline-flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              onClick={() => navigate(`/intranet/${a.id}/edit`, { state: { post: a } })}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              onClick={() => remove(a)}
                              disabled={deletingId === a.id}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className={`mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600 ${isOpen ? '' : 'line-clamp-3'}`}>
                        {a.body}
                      </p>
                      {a.body.length > 220 && (
                        <button onClick={() => setExpanded({ ...expanded, [a.id]: !isOpen })} className="mt-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                          {isOpen ? 'Show less' : 'Read more'}
                        </button>
                      )}
                      <p className="mt-3 text-xs text-slate-400">
                        {a.authorName ?? a.authorUsername ?? 'HR'} · {fmtDate(a.publishedAt)}
                      </p>
                    </article>
                  )
                })}
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <Pager
                  page={feedPage}
                  totalPages={feed?.totalPages ?? 1}
                  total={feed?.total ?? 0}
                  size={feedSize}
                  onPage={setFeedPage}
                  onSize={(s) => { setFeedSize(s); setFeedPage(1) }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* ---- SOP library ---- */}
      {tab === 'sop' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex gap-1 rounded-xl bg-slate-100 p-1 sm:w-fit">
              {(['sop', 'policy'] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => { setSopCategory(c); setDocPage(1) }}
                  className={`rounded-lg px-4 py-2 text-xs font-semibold capitalize transition-all ${sopCategory === c ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {c === 'sop' ? 'SOPs' : 'Policies'}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={docQuery}
                onChange={(e) => setDocQuery(e.target.value)}
                placeholder="Search documents…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
              />
            </div>
          </div>

          {docsError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {docsError}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className={`transition-opacity ${docsLoading ? 'opacity-60' : ''}`}>
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Document</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Size</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Uploaded</th>
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {docRows.map((doc) => (
                    <tr key={doc.id} className="transition-colors hover:bg-slate-50/70">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-[10px] font-extrabold text-brand-600 ring-1 ring-brand-100">
                            {extOf(doc.fileName)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{doc.title}</p>
                            <p className="truncate text-xs text-slate-400">{doc.fileName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{formatBytes(doc.fileSize)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{fmtDate(doc.createdAt)}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={() => download(doc)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                          title="Download"
                        >
                          <Download size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {docRows.length === 0 && !docsLoading && !docsError && (
              <div className="flex flex-col items-center py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <BookOpen size={22} />
                </div>
                <p className="mt-3 text-sm font-semibold">No {sopCategory === 'sop' ? 'SOPs' : 'policies'} yet</p>
                <p className="mt-1 text-xs text-slate-400">Upload a document with the {sopCategory} category to see it here.</p>
              </div>
            )}
            <Pager
              page={docPage}
              totalPages={docs?.totalPages ?? 1}
              total={docs?.total ?? 0}
              size={docSize}
              onPage={setDocPage}
              onSize={(s) => { setDocSize(s); setDocPage(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
